import axios from "axios";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminDashboard from "../AdminDashboard/admindash";
import {
  PlusCircle,
  MapPin,
  Globe,
  FileText,
  Tag,
  Image as ImageIcon,
  ArrowRight,
  Eye,
  Loader2
} from "lucide-react";
import { toast } from "react-toastify";
import API_URL from "../../config";


function Adddest() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inputs, setInputs] = useState({
    destinationName: "",
    country: "",
    description: "",
    category: "",
    image: "",
  });

  const handlechange = (e) => {
    setInputs((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const handlesubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/destinations`, {
        destinationName: String(inputs.destinationName),
        country: String(inputs.country),
        description: String(inputs.description),
        category: String(inputs.category),
        image: String(inputs.image),
      });
      toast.success("Destination added successfully!");
      setTimeout(() => navigate("/destinationview"), 1500);
    } catch (err) {
      console.error(err);
      toast.error("Failed to add destination. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminDashboard>
      <div className="font-sans">
        {/* Header Section */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-6">
          <div>
            <h1 className="text-2xl font-black text-sky-950 tracking-tight flex items-center gap-2">
              <PlusCircle className="text-sky-500" size={24} />
              Add Destination
            </h1>
            <p className="text-sm text-gray-400 font-bold mt-1">Grow the TravelAura collection with a new location.</p>
          </div>
          <button
            onClick={() => navigate("/destinationview")}
            className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-sky-600 hover:text-sky-700 transition-colors bg-sky-50 px-4 py-2 rounded-xl border border-sky-100"
          >
            View List <ArrowRight size={14} />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

          {/* Form Section */}
          <div className="lg:col-span-7">
            <form onSubmit={handlesubmit} className="space-y-6">

              {/* Destination Name */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Destination Name</label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                  <input
                    type="text"
                    name="destinationName"
                    onChange={handlechange}
                    value={inputs.destinationName}
                    placeholder="Ella Nine Arch Bridge"
                    required
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none text-sm placeholder:text-gray-300"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Province Dropdown */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Province</label>
                  <div className="relative">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                    <select
                      name="country"
                      onChange={handlechange}
                      value={inputs.country}
                      required
                      className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none appearance-none text-sm"
                    >
                      <option value="" disabled>Select Province</option>
                      <option value="Central Province">Central Province</option>
                      <option value="Eastern Province">Eastern Province</option>
                      <option value="North Central Province">North Central Province</option>
                      <option value="Northern Province">Northern Province</option>
                      <option value="North Western Province">North Western Province</option>
                      <option value="Sabaragamuwa Province">Sabaragamuwa Province</option>
                      <option value="Southern Province">Southern Province</option>
                      <option value="Uva Province">Uva Province</option>
                      <option value="Western Province">Western Province</option>
                    </select>
                  </div>
                </div>

                {/* Category Dropdown */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Category</label>
                  <div className="relative">
                    <Tag className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                    <select
                      name="category"
                      onChange={handlechange}
                      value={inputs.category}
                      required
                      className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none appearance-none text-sm"
                    >
                      <option value="" disabled>Select Category</option>
                      <option value="Beach">Beach</option>
                      <option value="Historical">Historical</option>
                      <option value="Adventure">Adventure</option>
                      <option value="Wildlife">Wildlife</option>
                      <option value="Cultural">Cultural</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Description</label>
                <div className="relative">
                  <FileText className="absolute left-4 top-4 text-sky-400" size={18} />
                  <textarea
                    name="description"
                    onChange={handlechange}
                    value={inputs.description}
                    placeholder="What makes this place special?"
                    required
                    rows="4"
                    className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none resize-none text-sm placeholder:text-gray-300"
                  ></textarea>
                </div>
              </div>

              {/* Image URL */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Featured Image Link</label>
                <div className="relative">
                  <ImageIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                  <input
                    type="text"
                    name="image"
                    onChange={handlechange}
                    value={inputs.image}
                    placeholder="Enter image URL..."
                    required
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none text-sm placeholder:text-gray-300"
                  />
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-sky-600 text-white font-black rounded-xl hover:bg-sky-700 active:scale-[0.98] transition-all shadow-lg shadow-sky-600/10 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed uppercase tracking-widest text-xs"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Publish Destination
                      <PlusCircle size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 mb-4 ml-2">
              <Eye size={16} className="text-sky-500" />
              <span className="text-[10px] font-black text-sky-950 uppercase tracking-widest">Live Preview</span>
            </div>

            <div className="bg-gray-50/50 border border-gray-100 rounded-2xl overflow-hidden group border-dashed">
              <div className="relative h-48 bg-white overflow-hidden">
                {inputs.image ? (
                  <img
                    src={inputs.image}
                    alt="Preview"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    onError={(e) => {
                      e.target.src = "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?q=80&w=1000&auto=format&fit=crop";
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 gap-2">
                    <ImageIcon size={32} />
                    <p className="text-[10px] font-black uppercase tracking-widest">No Image Provided</p>
                  </div>
                )}
                {inputs.category && (
                  <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/90 backdrop-blur rounded-lg text-[9px] font-black uppercase tracking-widest text-sky-600 shadow-sm">
                    {inputs.category}
                  </div>
                )}
              </div>

              <div className="p-6">
                <div className="flex items-center gap-2 text-sky-500 mb-2">
                  <Globe size={12} />
                  <span className="text-[9px] font-black uppercase tracking-widest">
                    {inputs.country || "Province Not Selected"}
                  </span>
                </div>
                <h2 className="text-xl font-black text-sky-950 mb-3 truncate">
                  {inputs.destinationName || "Untitiled Destination"}
                </h2>
                <p className="text-gray-400 text-xs font-bold leading-relaxed line-clamp-3 mb-6">
                  {inputs.description || "Start typing in the form to see your destination content come to life here..."}
                </p>

                <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 font-bold text-[8px] uppercase tracking-tighter">TA</div>
                    <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest">Platform Admin</span>
                  </div>
                  <div className="w-8 h-1 bg-sky-100 rounded-full"></div>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-amber-50 rounded-xl p-4 border border-amber-100/50">
              <p className="text-[10px] font-bold text-amber-800 leading-relaxed italic text-center">
                "Add a high-quality image URL to make this destination truly stand out to travelers!"
              </p>
            </div>
          </div>

        </div>
      </div>
    </AdminDashboard>
  );
}

export default Adddest;
